import { useMemo, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import {
    useLocationTypes,
    useCreateLocationType,
    useUpdateLocationType,
    useDeleteLocationType,
    type LocationType,
} from '@/api/locationTypes';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { FormField, Input } from '@/components/ui/FormField';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
type StatusTab = 'Active' | 'Inactive' | 'All';
const STATUS_TABS: StatusTab[] = ['Active', 'Inactive', 'All'];

export function LocationTypesPanel() {
    const { data, isLoading, isError, error } = useLocationTypes();
    const createM = useCreateLocationType();
    const updateM = useUpdateLocationType();
    const deleteM = useDeleteLocationType();

    const [tab, setTab] = useState<StatusTab>('Active');
    const [editing, setEditing] = useState<LocationType | null>(null);
    const [creating, setCreating] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<LocationType | null>(null);

    const rows = useMemo(() => {
        if (!data) return [];
        const filtered = tab === 'All' ? data : data.filter((r) => r.status === tab);
        return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    }, [data, tab]);

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <h2 className="font-display text-lg font-bold text-text">Location Types</h2>
                <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                    <Plus className="w-3.5 h-3.5" /> Add location type
                </Button>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                Where sessions happen (e.g., Clinic, Home, School). POS is the CMS Place of
                Service code used on claims.
            </p>

            <Card className="p-0 overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <div role="tablist" aria-label="Filter location types by status" className="flex items-center gap-1 p-0.5 bg-surface-muted rounded-md">
                        {STATUS_TABS.map((s) => (
                            <button
                                key={s}
                                type="button"
                                role="tab"
                                aria-selected={tab === s}
                                onClick={() => setTab(s)}
                                className={cn(
                                    'px-3 py-1 text-xs rounded transition-colors',
                                    tab === s ? 'bg-surface text-text shadow-xs font-medium' : 'text-text-muted hover:text-text',
                                )}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="ml-auto text-xs text-text-subtle tabular-nums">
                        {rows.length} {rows.length === 1 ? 'location' : 'locations'}
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-12 flex items-center justify-center text-text-subtle">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load location types</p>
                        <p className="text-xs text-text-subtle">{extractErrorMessage(error, 'Please try again.')}</p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-text-subtle">
                        <p>{data && data.length > 0 ? 'No location types match the current filter.' : 'No location types yet.'}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Name</th>
                                    <th className="px-3 py-2 font-bold w-28">POS</th>
                                    <th className="px-3 py-2 font-bold w-28">Status</th>
                                    <th className="px-3 py-2 font-bold w-24 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-surface-muted transition-colors">
                                        <td className="px-3 py-2 font-medium">{r.name}</td>
                                        <td className="px-3 py-2 font-mono text-xs text-text-muted">{r.pos || '—'}</td>
                                        <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                                        <td className="px-3 py-2 text-right">
                                            <Button variant="ghost" size="xs" aria-label={`Edit ${r.name}`} onClick={() => setEditing(r)} className="mr-1">
                                                <Pencil className="w-3 h-3" />
                                            </Button>
                                            <Button variant="ghost" size="xs" aria-label={`Delete ${r.name}`} onClick={() => setConfirmDelete(r)}>
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {(creating || editing) && (
                <LocationTypeDialog
                    initial={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSubmit={async (payload) => {
                        if (editing) await updateM.mutateAsync({ id: editing.id, payload });
                        else await createM.mutateAsync(payload);
                    }}
                />
            )}

            {confirmDelete && (
                <DeleteConfirmDialog
                    row={confirmDelete}
                    onClose={() => setConfirmDelete(null)}
                    onConfirm={async () => {
                        await deleteM.mutateAsync(confirmDelete.id);
                        setConfirmDelete(null);
                    }}
                />
            )}
        </div>
    );
}

interface DialogPayload { name: string; pos?: string; status: 'Active' | 'Inactive' }

function LocationTypeDialog({
    initial,
    onClose,
    onSubmit,
}: {
    initial: LocationType | null;
    onClose: () => void;
    onSubmit: (p: DialogPayload) => Promise<void>;
}) {
    const [name, setName] = useState(initial?.name ?? '');
    const [pos, setPos] = useState(initial?.pos ?? '');
    const [status, setStatus] = useState<'Active' | 'Inactive'>(initial?.status ?? 'Active');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        if (!name.trim()) { setErr('Name is required'); return; }
        setSaving(true);
        try {
            await onSubmit({ name: name.trim(), pos: pos.trim() || undefined, status });
            onClose();
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{initial ? 'Edit location type' : 'Add location type'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <FormField label="Name" required error={err ?? undefined}>
                        {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
                    </FormField>
                    <div className="grid grid-cols-2 gap-3">
                        <FormField label="POS code" hint="Place of Service">
                            {(id) => <Input id={id} value={pos} onChange={(e) => setPos(e.target.value)} />}
                        </FormField>
                        <FormField label="Status">
                            {(id) => (
                                <select
                                    id={id}
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive')}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                            )}
                        </FormField>
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button variant="primary" size="sm" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : initial ? 'Save' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function DeleteConfirmDialog({
    row,
    onClose,
    onConfirm,
}: {
    row: LocationType;
    onClose: () => void;
    onConfirm: () => Promise<void>;
}) {
    const [working, setWorking] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Delete location type?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-text-muted">
                    Delete <span className="font-medium text-text">{row.name}</span>? If this
                    location is in use on any session the delete will be refused.
                </p>
                {err && <p className="mt-2 text-xs text-error">{err}</p>}
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="ghost" size="sm" type="button">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="danger"
                        size="sm"
                        disabled={working}
                        onClick={async () => {
                            setErr(null);
                            setWorking(true);
                            try { await onConfirm(); }
                            catch (e) { setErr(extractErrorMessage(e, 'Could not delete.')); }
                            finally { setWorking(false); }
                        }}
                    >
                        {working ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
